import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import { initialLocalCaseTypesGridState, LocalCaseTypesGridState } from '@/modules/configurationModule/detail/local-cases/local-case-types/components/local-case-types-grid/local-case-types-grid.model'
import { caseTypeLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/local-cases/local-case-types/services/local-case-types.service'
import { ConfigurationCaseTypeLaboratoryPatchInBatchRequest, ConfigurationCaseTypeLaboratoryResponse } from '@/modules/configurationModule/detail/local-cases/local-case-types/services/local-case-types.dto'
import LocalCaseTypesModal from '@/modules/configurationModule/detail/local-cases/local-case-types/components/local-case-types-modal/local-case-types-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        LocalCaseTypesModal
    }
})

export default class LocalCaseTypesGrid extends BaseCrudServerSideComponent<ConfigurationCaseTypeLaboratoryResponse> implements BaseCrud {
    @Ref() readonly localCaseTypesModalWrapper!: any

    state: LocalCaseTypesGridState
    constructor() {
        super()
        this.state = initialLocalCaseTypesGridState()
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected: StrictNullable<RowNode> = params?.node
                    const selectedRow: ConfigurationCaseTypeLaboratoryResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.localCaseTypesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationCaseTypeLaboratoryResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationCaseTypeLaboratoryResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationCaseTypeLaboratoryPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationCaseTypeLaboratoryResponse) => {
            return {
                id: row.id,
                description: row.description,
                active: option,
                serial: row.serial
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(caseTypeLaboratoriesConfigurationServices.patchCaseTypeLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }
    async createOperation(row: ConfigurationCaseTypeLaboratoryResponse): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(caseTypeLaboratoriesConfigurationServices.createCaseTypeLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localCaseTypesModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }
    async patchOperation(rows: ConfigurationCaseTypeLaboratoryResponse[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(caseTypeLaboratoriesConfigurationServices.patchCaseTypeLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }
    async deleteOperation(rows: ConfigurationCaseTypeLaboratoryResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize: number = this.state?.selectedRows?.length
            const labelConfirmation: string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion: string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction: string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }
    private async _deleteBatchOperation(rows: ConfigurationCaseTypeLaboratoryResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationCaseTypeLaboratoryResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(caseTypeLaboratoriesConfigurationServices.deleteCaseTypeLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
